import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { authApi } from "@/client/features/auth/auth-api";
import { homePathForUser, useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";
import { InvalidTokenCard, TokenCheckingCard } from "@/client/pages/auth/invalid-token-card";
import { MfaChallenge } from "@/client/pages/auth/mfa-challenge";
import { useTokenStatus } from "@/client/pages/auth/use-token-status";
import { PASSWORD_POLICY_HINT, passwordSchema } from "@/shared/password";

const schema = z
  .object({
    name: z.string().min(1, "Enter your name"),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type Values = z.infer<typeof schema>;

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  const tokenStatus = useTokenStatus(token, authApi.checkInviteToken);
  const [mfa, setMfa] = useState(false);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", password: "", confirmPassword: "" },
  });

  if (mfa) {
    return (
      <MfaChallenge
        enrolling
        onComplete={(user) => navigate(homePathForUser(user), { replace: true })}
      />
    );
  }

  if (tokenStatus === "checking") {
    return <TokenCheckingCard />;
  }

  if (!token || tokenStatus === "invalid") {
    return (
      <InvalidTokenCard
        title="Invitation link invalid"
        description="This invitation link is invalid, expired, or already used. Ask your administrator to resend it."
      />
    );
  }

  const onSubmit = async (values: Values) => {
    try {
      const response = await authApi.acceptInvite({
        token,
        name: values.name,
        password: values.password,
      });
      if ("mfaRequired" in response) {
        setMfa(true);
        return;
      }
      const { user, features, deactivated } = response;
      setUser(user, features, deactivated);
      navigate(homePathForUser(user), { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to accept invitation. Please try again.";
      toast.error(message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accept your invitation</CardTitle>
        <CardDescription>Set a name and password to activate your account.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full name</FormLabel>
                  <FormControl>
                    <Input autoComplete="name" placeholder="Ada Lovelace" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <p className="text-muted-foreground text-xs">{PASSWORD_POLICY_HINT}</p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Activating…" : "Activate account"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
